/** Which cards are answered together, and what is left: the counter and the "answer N more" line. */
import type { EventForTeam } from "@bsim/contracts";

/** A card the team can see and answer (not held back, not a facilitator's cancelled one). */
export const LIVE = new Set<EventForTeam["status"]>(["delivered", "responded", "defaulted"]);

/** The cards of one sitting: those the year paused at in the same month. */
export function sameSitting(events: readonly EventForTeam[], card: EventForTeam): readonly EventForTeam[] {
  const month = (e: EventForTeam): number | null => (e.timing.phase === "midRound" ? e.timing.month : null);
  return events.filter((e) => LIVE.has(e.status) && e.mode === card.mode && month(e) === month(card));
}

export interface Position { readonly n: number; readonly of: number }

/** "Card n of m" for a card in its group; none for a card on its own. */
export function positionIn(group: readonly EventForTeam[], card: EventForTeam): Position | undefined {
  const index = group.findIndex((e) => e.injectionId === card.injectionId);
  return index < 0 || group.length < 2 ? undefined : { n: index + 1, of: group.length };
}

/** The other cards of the sitting still unanswered; this card counts as answered once `answered` (its response may not be in the team state yet). Source of both "Card n of m" and the footer. */
export const othersLeft = (sitting: readonly EventForTeam[], card: EventForTeam): number => sitting.filter((e) => e.injectionId !== card.injectionId && e.response === null).length;

/** The footer line under a card: "Answer 1 more card after this one" while this card is open, "Answer 1 more card" once it is answered; none when nothing is left. */
export function answerMoreLine(others: number, thisOpen = false): string | null {
  if (others === 0) return thisOpen ? "Answer this card to continue the year" : null;
  return `Answer ${others} more ${others === 1 ? "card" : "cards"}${thisOpen ? " after this one" : ""}`;
}

/** The id an answer carries when the team took the card's usual outcome rather than one of its options. */
export const DEFAULT_ID = "default";

/** What a card was answered with: an option (its id and label), or the card's usual outcome taken as the team's own answer (`DEFAULT_ID`, which is no option of the card); none while unanswered. A stored response is always an answer someone gave: a card nobody answered has none. */
export function answeredWith(e: EventForTeam): { readonly id: string; readonly label: string } | undefined {
  const id = e.response?.choiceId;
  if (id === undefined) return undefined;
  if (id === DEFAULT_ID) return { id, label: `${e.card.defaultChoice.label} (the usual outcome)` };
  const choice = e.card.choices.find((c) => c.id === id);
  return choice === undefined ? undefined : { id, label: choice.label };
}
